<script setup lang="ts">
import { computed, ref } from 'vue';
import { useChatStore, type UiToolBlock } from '@/stores/chat';
import { toolIcon, toolLabel, toolTierLabel } from '@/utils/chatTools';
import MarkdownContent from './MarkdownContent.vue';

const props = defineProps<{ block: UiToolBlock }>();
const chat = useChatStore();
const open = ref(false);

const label = computed(() => toolLabel(props.block.name));
const icon = computed(() => toolIcon(props.block.name));
const tierLabel = computed(() => toolTierLabel(props.block.tier));
const busy = computed(() => props.block.status === 'preparing' || props.block.status === 'running');
const awaiting = computed(() => props.block.status === 'awaiting');
const confirmation = computed(() => props.block.confirmation);
const pending = computed(
  () => confirmation.value?.status === 'pending' || confirmation.value?.status === 'submitting',
);

/** Uitkomst van een write/delete-tool, ook na herladen uit de database (dan alleen via de uitvoer). */
const outcome = computed<'confirmed' | 'cancelled' | 'expired' | 'editor' | null>(() => {
  if (props.block.tier === 'read') return null;
  const c = confirmation.value?.status;
  if (c === 'confirmed' || c === 'cancelled' || c === 'expired') return c;
  const out = props.block.output;
  if (!out) return null;
  try {
    const j = JSON.parse(out) as { cancelled?: boolean; reason?: string; mode?: string };
    if (j.cancelled) return j.reason === 'expired' ? 'expired' : 'cancelled';
    if (j.mode === 'editor') return 'editor';
  } catch {
    /* geen JSON */
  }
  return props.block.status === 'done' ? 'confirmed' : null;
});
const outcomeText: Record<NonNullable<typeof outcome.value>, string> = {
  confirmed: 'Bevestigd en uitgevoerd',
  cancelled: 'Geannuleerd',
  expired: 'Verlopen (niet uitgevoerd)',
  editor: 'Als concept in het formulier gezet — controleer en sla op',
};

function decide(decision: 'confirm' | 'cancel') {
  void chat.confirmTool(props.block.id, decision);
}

function pretty(v: unknown, max = 4000): string {
  let s: string;
  if (typeof v === 'string') {
    try {
      s = JSON.stringify(JSON.parse(v), null, 2);
    } catch {
      s = v;
    }
  } else s = JSON.stringify(v ?? null, null, 2);
  return s.length > max ? `${s.slice(0, max)}\n… (${s.length - max} tekens meer)` : s;
}
</script>

<template>
  <div class="kl-chat-tool" :class="[block.status, `tier-${block.tier}`, { open }]" data-testid="chat-tool">
    <button
      type="button"
      class="kl-chat-tool-head"
      :aria-expanded="open"
      :title="block.name"
      @click="open = !open"
    >
      <span v-if="busy" class="spinner-border spinner-border-sm text-primary" aria-hidden="true"></span>
      <i v-else-if="awaiting" class="bi bi-question-circle-fill text-warning" aria-hidden="true"></i>
      <i
        v-else
        class="bi"
        :class="
          block.status === 'error'
            ? 'bi-exclamation-triangle-fill text-danger'
            : outcome === 'cancelled' || outcome === 'expired'
              ? 'bi-slash-circle text-muted'
              : 'bi-check-circle-fill text-success'
        "
        aria-hidden="true"
      ></i>
      <i class="bi" :class="`bi-${icon}`" aria-hidden="true"></i>
      <span class="kl-chat-tool-label">{{ label }}</span>
      <span v-if="tierLabel" class="kl-chat-tier" :class="`tier-${block.tier}`">{{ tierLabel }}</span>
      <span v-if="busy" class="text-muted small">bezig…</span>
      <span v-else-if="awaiting" class="text-warning small">wacht op bevestiging</span>
      <i class="bi ms-auto" :class="open ? 'bi-chevron-up' : 'bi-chevron-down'" aria-hidden="true"></i>
    </button>

    <!-- Bevestigingskaart: de gebruiker beslist, de stream loopt intussen door -->
    <div v-if="confirmation && pending" class="kl-chat-confirm" data-testid="chat-confirm">
      <div class="fw-semibold">
        <i
          class="bi me-1"
          :class="block.tier === 'delete' ? 'bi-trash text-danger' : 'bi-pencil-square text-warning'"
          aria-hidden="true"
        ></i>
        {{ confirmation.summary }}
      </div>
      <MarkdownContent
        v-if="confirmation.details"
        :content="confirmation.details"
        class="kl-chat-confirm-details"
      />
      <div class="d-flex gap-2 mt-2">
        <button
          type="button"
          class="btn btn-sm"
          :class="block.tier === 'delete' ? 'btn-danger' : 'btn-primary'"
          :disabled="confirmation.status === 'submitting'"
          data-testid="chat-confirm-yes"
          @click="decide('confirm')"
        >
          <span
            v-if="confirmation.status === 'submitting'"
            class="spinner-border spinner-border-sm me-1"
            aria-hidden="true"
          ></span>
          Bevestigen
        </button>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          :disabled="confirmation.status === 'submitting'"
          data-testid="chat-confirm-no"
          @click="decide('cancel')"
        >
          Annuleren
        </button>
      </div>
    </div>
    <div v-else-if="outcome" class="kl-chat-confirm-result small text-muted" data-testid="chat-tool-outcome">
      <i
        class="bi me-1"
        :class="outcome === 'confirmed' || outcome === 'editor' ? 'bi-check2' : 'bi-slash-circle'"
        aria-hidden="true"
      ></i
      >{{ outcomeText[outcome] }}
    </div>

    <div v-if="open" class="kl-chat-tool-body">
      <div class="small text-muted mb-1">Invoer</div>
      <pre class="kl-chat-tool-pre">{{ pretty(block.input) }}</pre>
      <template v-if="block.output !== null">
        <div class="small text-muted mb-1">{{ block.status === 'error' ? 'Fout' : 'Resultaat' }}</div>
        <pre class="kl-chat-tool-pre">{{ pretty(block.output) }}</pre>
      </template>
    </div>
  </div>
</template>
